import type { ReactElement } from 'react';
import type { PlanTier } from '@shared/types';
import { tierDisplayName } from '@shared/tiers';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { money } from '../../lib/format';
import { activeTier, flightDateRange, pricedPlan, productLabel } from '../../lib/plan';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { buildInvestmentTable } from './investmentRows';
import { Frame, StatTile, Table } from './primitives';
import type { TableColumn } from './primitives';

// No Dates column: every line runs the whole flight, so it only ever
// repeated one value down the page. The flight is stated once, on the term
// tile. (Two columns: this table left, the tiles stacked right.)
const COLUMNS: TableColumn[] = [
  { key: 'label', label: 'Line item', width: 'minmax(0, 1fr)' },
  { key: 'budget', label: 'Monthly', align: 'right', width: 'auto' },
];

/** The tier this page presents: its own `section.tierId`, else the active one. */
function tierForSection(proposal: PageProps['proposal'], tierId: string | undefined): PlanTier | null {
  if (!tierId) return activeTier(proposal);
  return proposal.budget.tiers.find((tier) => tier.id === tierId) ?? null;
}

/**
 * budget_summary (brief §4 `17 investment`): FULL light, table + tiles. The
 * deck carries ONE OF THESE PER BUDGET TIER — Essential, Growth, Accelerate
 * (state/document.ts buildBudgetSummarySections) — so each page presents a
 * single version of the plan rather than pills that re-derive one shared
 * page. `section.tierId` says which; a page without one (a proposal with no
 * tiers priced yet) falls back to the active plan.
 *
 * The tier NAME is resolved from the tier id via domain/tiers.ts, never from
 * the stored `PlanTier.label`, so a proposal whose tiers were generated under
 * an older name set (Lean/Recommended/Aggressive, or Good/Better/Best) still
 * reads Essential/Growth/Accelerate.
 *
 * The option's own name is the whole headline — "Essential", "Growth",
 * "Accelerate" — and nothing else sits in the header: no "INVESTMENT · N OF
 * 3" eyebrow, no "<tier> — $X/mo" price pill, no tier-select button (user
 * decision, 2026-09-22 — the header read as good/better/best sales chrome).
 * A tier is now chosen through the agent (`update_proposal` select_tier),
 * not from this page. The legacy no-tier fallback page (no `section.tierId`)
 * keeps the plain "Investment" headline, since there is only one such page.
 *
 * Layout (2026-09-28, all three tier pages): two columns — the line-item
 * table on the left takes the full body height, the two totals tiles stack
 * on the right. The old table-over-tiles stack clipped the "Monthly total"
 * row on any tier with 7+ rows (Growth/Accelerate on 8-line plans); the
 * row cap in buildInvestmentTable is sized to THIS layout.
 *
 * Fees never appear: no management-fee row, no one-time-setup tile. The
 * table's total is `subtotalMonthly` and the term tile is that times the
 * flight length, so the visible rows always add up to the visible totals
 * (see buildInvestmentTable).
 */
export function BudgetSummaryPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const tier = tierForSection(proposal, section.tierId);
  const plan = tier?.pricing ?? (section.tierId ? null : pricedPlan(proposal));
  // Re-derived from the tier id, never `tier.label`: a stored tier still
  // carries whatever name it was generated under.
  const headline = tier ? tierDisplayName(tier.id) : 'Investment';
  const { productName } = useContentLibrary();
  const labelFor = (productId: string) => productName(productId) ?? productLabel(proposal, productId);
  const flightRange = flightDateRange(proposal);
  const table = plan ? buildInvestmentTable(plan, labelFor, flightRange ?? `${proposal.budget.durationMonths} months`) : null;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-budget-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-budget-body">
          <div className="mv3-budget-head">
            <h1 className="mv3-h1">{headline}</h1>
          </div>
          <div className="mv3-budget-intro">
            <EditableText
              value={section.content.body}
              field="body"
              placeholder="Summarize the investment"
              readOnly={readOnly}
              onSave={(body) => actions.saveContent(section, { body })}
            />
          </div>
          {table ? (
            <div className="mv3-budget-columns">
              <Table
                className="mv3-budget-table"
                columns={COLUMNS}
                rows={table.rows.map((row) => ({
                  label: row.label,
                  budget: money(row.monthlyBudget),
                }))}
                totalRow={{ label: table.totalRow.label, budget: money(table.totalRow.monthlyBudget) }}
              />
              <div className="mv3-budget-tiles">
                <StatTile label="Monthly investment" value={money(table.totalRow.monthlyBudget)} />
                <StatTile
                  label={`Total over ${proposal.budget.durationMonths} months`}
                  value={money(table.totalOverTerm)}
                  note={flightRange ?? undefined}
                  className="mv3-stat-tile-accent"
                />
              </div>
            </div>
          ) : (
            <p className="note">The investment table appears once the plan has been priced.</p>
          )}
        </div>
      </Frame>
    </PageShell>
  );
}
